import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BackupStatusResponse,
  getBackupStatus,
  restoreCollection,
  restoreSnapshot,
  RestoreCollectionResponse,
} from '../../utils/BackupAPI';
import './BackupManagement.css';
import BrandMarkSpinner from '../Loading/BrandMarkSpinner';

const COLLECTION_LABELS: Record<string, string> = {
  galleries: 'Galleries',
  categories: 'Categories',
  services: 'Services',
  addons: 'Addons',
  blog_posts: 'Blog Posts',
  blog_categories: 'Blog Categories',
  service_customization: 'Service Customization',
  share_image: 'Share Image',
  search_photos: 'Photos for Google',
};

const INITIAL_VISIBLE_VERSIONS = 5;

const describeReason = (reason?: string, isFileRow = false): string => {
  if (isFileRow) return 'Current file state';
  switch (reason) {
    case 'mongo_after_update':
    case 'mongo_update':
      return 'Saved update';
    case 'mongo_before_update':
      return 'Pre-update checkpoint';
    case 'restore_collection':
      return 'Restore from file';
    case 'restore_snapshot':
      return 'Restore from snapshot';
    case 'file_before_update':
      return 'Pre-update checkpoint (file)';
    case 'file_after_update':
      return 'Saved update (file)';
    default:
      return reason ? `Event: ${reason}` : 'Snapshot';
  }
};

const BackupManagement: React.FC = () => {
  const [status, setStatus] = useState<BackupStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeRestoreKey, setActiveRestoreKey] = useState<string | null>(null);
  const [activeSnapshotId, setActiveSnapshotId] = useState<string | null>(null);
  const [typedPhrase, setTypedPhrase] = useState('');
  const [restoringKey, setRestoringKey] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const backupStatus = await getBackupStatus();
      setStatus(backupStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load backup status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const collectionEntries = useMemo(() => {
    if (!status) return [];
    return Object.entries(status.collections);
  }, [status]);

  const groupedRestoreVersions = useMemo(() => {
    if (!status) return [];

    return Object.entries(status.collections)
      .map(([collectionKey, collectionState]) => {
        const collectionLabel = COLLECTION_LABELS[collectionKey] || collectionKey;
        const snapshotRows = [...(collectionState.snapshots || [])]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .map((snapshot) => ({
            rowId: `${collectionKey}::${snapshot.snapshotId}`,
            collectionKey,
            snapshotId: snapshot.snapshotId,
            sourceLabel: `Snapshot`,
            reason: snapshot.reason,
            sourceSnapshotId: snapshot.sourceSnapshotId || null,
            createdAt: snapshot.createdAt,
            count: snapshot.count,
          }));

        // Use snapshot history as the source of truth for restore versions.
        // Only fall back to current file state if no snapshots exist yet.
        const baseRows = snapshotRows.length > 0
          ? snapshotRows
          : [
              {
                rowId: `${collectionKey}::file`,
                collectionKey,
                snapshotId: null as string | null,
                sourceLabel: 'Current file backup',
                reason: 'file_current',
                sourceSnapshotId: null as string | null,
                createdAt: collectionState.fileBackup.mtime || collectionState.filePrimary.mtime || null,
                count: Math.max(collectionState.fileBackup.count || 0, collectionState.filePrimary.count || 0),
              },
            ];

        const orderedNewestFirst = [...baseRows].sort((a, b) => {
          const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return timeB - timeA;
        });

        // Number versions from oldest -> newest so version IDs only increase over time.
        const orderedOldestFirst = [...orderedNewestFirst].reverse();
        const versionsWithContextOldestFirst = orderedOldestFirst.map((version, index) => {
          const older = orderedOldestFirst[index - 1];
          const delta = older ? version.count - older.count : 0;
          let deltaLabel = 'Baseline snapshot';
          if (older) {
            if (delta > 0) deltaLabel = `Added ${delta} record${delta === 1 ? '' : 's'}`;
            else if (delta < 0) deltaLabel = `Deleted ${Math.abs(delta)} record${Math.abs(delta) === 1 ? '' : 's'}`;
            else deltaLabel = 'No count change';
          }
          const versionNumber = `v${index + 1}`;
          return {
            ...version,
            versionNumber,
            reasonLabel: describeReason(version.reason, !version.snapshotId),
            deltaLabel,
            sourceLabel: version.snapshotId ? `${versionNumber} - Snapshot` : `${versionNumber} - Current file`,
          };
        });

        const snapshotIdToVersion = new Map(
          versionsWithContextOldestFirst
            .filter((version) => Boolean(version.snapshotId))
            .map((version) => [String(version.snapshotId), String(version.versionNumber)])
        );

        const versionsWithRestoreFromContext = versionsWithContextOldestFirst.map((version) => {
          let restoreVersionContext = '';
          if (version.reason === 'restore_snapshot') {
            const sourceVersion = version.sourceSnapshotId
              ? snapshotIdToVersion.get(String(version.sourceSnapshotId))
              : undefined;
            restoreVersionContext = sourceVersion
              ? `Restored from ${sourceVersion}`
              : 'Restored from selected snapshot';
          } else if (version.reason === 'restore_collection') {
            restoreVersionContext = 'Restored from current file backup';
          }

          return {
            ...version,
            restoreVersionContext,
          };
        });

        const versionsWithContextNewestFirst = [...versionsWithRestoreFromContext].reverse();

        return {
          collectionKey,
          collectionLabel,
          versions: versionsWithContextNewestFirst,
        };
      })
      .sort((a, b) => {
        const latestA = a.versions[0]?.createdAt ? new Date(a.versions[0].createdAt).getTime() : 0;
        const latestB = b.versions[0]?.createdAt ? new Date(b.versions[0].createdAt).getTime() : 0;
        if (latestA !== latestB) return latestB - latestA;
        return a.collectionLabel.localeCompare(b.collectionLabel);
      });
  }, [status]);

  const closeModal = () => {
    setActiveRestoreKey(null);
    setActiveSnapshotId(null);
    setTypedPhrase('');
  };

  const openRestoreCollectionModal = (collectionKey: string, snapshotId: string | null = null) => {
    setActiveRestoreKey(collectionKey);
    setActiveSnapshotId(snapshotId);
    setTypedPhrase('');
  };

  const handleRestore = async () => {
    if (!activeRestoreKey || !status) return;

    setRestoringKey(activeRestoreKey);
    setResultMessage('');
    try {
      let result: RestoreCollectionResponse;
      if (activeSnapshotId) {
        result = await restoreSnapshot(activeRestoreKey, activeSnapshotId, typedPhrase);
      } else {
        result = await restoreCollection(activeRestoreKey, typedPhrase);
      }
      const collectionLabel = COLLECTION_LABELS[activeRestoreKey] || activeRestoreKey;
      const requestedVersionLabel = activeSnapshotId
        ? `Snapshot ID ${activeSnapshotId}`
        : 'Current file backup';
      const sourceLabel = result.restoreContext?.sourceVersionLabel
        || (activeSnapshotId ? `Snapshot ${activeSnapshotId}` : `${result.sourceType || 'backup'} source`);
      const sourceMeta = result.restoreContext?.sourceSnapshotCreatedAt
        ? ` (${new Date(result.restoreContext.sourceSnapshotCreatedAt).toLocaleString()})`
        : '';
      const sourceFileName = result.sourceSnapshot?.fileName
        || (result.restoreContext?.sourcePath
          ? result.restoreContext.sourcePath.split(/[\\/]/).pop()
          : (result.sourcePath ? result.sourcePath.split(/[\\/]/).pop() : null));
      const sourceFileMeta = sourceFileName ? ` | source file: ${sourceFileName}` : '';
      const reasonMeta = result.restoreContext?.sourceSnapshotReason
        ? ` | reason: ${result.restoreContext.sourceSnapshotReason}`
        : '';
      const countMeta = (result.restoreContext?.preMongoCount !== undefined && result.restoreContext?.postMongoCount !== undefined)
        ? ` | mongo ${result.restoreContext.preMongoCount} -> ${result.restoreContext.postMongoCount}`
        : '';
      const newVersionMeta = result.restoreContext?.createdSnapshotId
        ? ` | new restore version: ${result.restoreContext.createdSnapshotId}`
        : '';
      setResultMessage(
        `Restored ${collectionLabel}: ${result.restoredCount} records. Requested version: ${requestedVersionLabel}. Applied source: ${sourceLabel}${sourceMeta}${sourceFileMeta}${reasonMeta}${countMeta}${newVersionMeta}.`
      );
      closeModal();
      await loadStatus();

      // Refresh relevant admin sections after restore so UI reflects server state immediately.
      const refreshEventByCollection: Record<string, string | null> = {
        galleries: 'galleries_updated',
        categories: 'categories_updated',
        blog_posts: null,
        blog_categories: null,
        services: null,
        addons: null,
        service_customization: null,
        share_image: null,
      };
      const refreshEvent = refreshEventByCollection[activeRestoreKey];
      if (refreshEvent) {
        window.dispatchEvent(new CustomEvent(refreshEvent));
      }
    } catch (err) {
      setResultMessage(err instanceof Error ? err.message : 'Restore failed');
    } finally {
      setRestoringKey(null);
    }
  };

  return (
    <section className="admin-section backup-management">
      <div className="section-header">
        <h2>Backups</h2>
        <div className="section-controls">
          <button className="btn btn-secondary" onClick={loadStatus} disabled={loading}>
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
      </div>

      {loading && <BrandMarkSpinner size="small" label="Loading backup status" caption />}
      {error && <div className="backup-error">{error}</div>}

      {status && (
        <>
          <div className="backup-summary">
            <div><strong>Current Storage:</strong> {status.storage}</div>
            <div><strong>Mongo Connected:</strong> {status.mongoConnected ? 'Yes' : 'No'}</div>
            <div><strong>Data Directory:</strong> {status.dataDir}</div>
            <div><strong>Last Checked:</strong> {new Date(status.timestamp).toLocaleString()}</div>
          </div>

          {status.lastMongoConnectionError && (
            <div className="backup-warning">
              <strong>Last Mongo Error:</strong> {status.lastMongoConnectionError.message}
            </div>
          )}

          {resultMessage && <div className="backup-result">{resultMessage}</div>}

          <div className="backup-versions-simple">
            <h3>Available Restore Versions on Render</h3>
            <p className="backup-versions-hint">
              Newest versions are shown first. Snapshot retention is {status.retentionLimit} per collection.
            </p>
            <div className="backup-versions-table-simple">
              {groupedRestoreVersions.map((group) => (
                <div className="backup-type-group" key={group.collectionKey}>
                  <div className="backup-type-heading">{group.collectionLabel}</div>
                  <div className="backup-versions-head-simple">
                    <span>Version</span>
                    <span>Created</span>
                    <span>Records</span>
                    <span>Context</span>
                    <span>Restore</span>
                  </div>
                  {(expandedGroups[group.collectionKey]
                    ? group.versions
                    : group.versions.slice(0, INITIAL_VISIBLE_VERSIONS)
                  ).map((row) => (
                    <div className="backup-versions-row-simple" key={row.rowId}>
                      <span>
                        <strong>{row.sourceLabel}</strong>
                        {row.snapshotId && (
                          <div className="backup-version-id">{row.snapshotId}</div>
                        )}
                      </span>
                      <span>
                        {row.createdAt ? new Date(row.createdAt).toLocaleString() : 'n/a'}
                      </span>
                      <span>{row.count}</span>
                      <span>
                        <div>{row.reasonLabel}</div>
                        {row.restoreVersionContext && (
                          <div className="backup-version-context">{row.restoreVersionContext}</div>
                        )}
                        <div className="backup-version-context">{row.deltaLabel}</div>
                      </span>
                      <span>
                        <button
                          className="btn btn-danger btn-sm"
                          disabled={!status.mongoConnected || restoringKey === row.collectionKey}
                          onClick={() => openRestoreCollectionModal(row.collectionKey, row.snapshotId)}
                        >
                          Restore
                        </button>
                      </span>
                    </div>
                  ))}
                  {group.versions.length > INITIAL_VISIBLE_VERSIONS && (
                    <div className="backup-show-more-row">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() =>
                          setExpandedGroups((prev) => ({
                            ...prev,
                            [group.collectionKey]: !prev[group.collectionKey],
                          }))
                        }
                      >
                        {expandedGroups[group.collectionKey]
                          ? 'Show less'
                          : `Show more (${group.versions.length - INITIAL_VISIBLE_VERSIONS} older)`}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button
            className="btn btn-secondary backup-advanced-toggle"
            onClick={() => setShowAdvanced((prev) => !prev)}
          >
            {showAdvanced ? 'Hide Advanced Backup Details' : 'Show Advanced Backup Details'}
          </button>

          {showAdvanced && (
            <div className="backup-grid">
              {collectionEntries.map(([collectionKey, collectionState]) => (
                <div key={collectionKey} className="backup-card">
                  <h3>{COLLECTION_LABELS[collectionKey] || collectionKey}</h3>
                  <p><strong>Mongo Count:</strong> {collectionState.mongoCount ?? 'n/a'}</p>
                  <p><strong>Primary File Count:</strong> {collectionState.filePrimary.count}</p>
                  <p><strong>Backup File Count:</strong> {collectionState.fileBackup.count}</p>
                  <p><strong>Snapshot Retention:</strong> {collectionState.snapshots?.length || 0} / {status.retentionLimit}</p>
                  <p><strong>Primary Updated:</strong> {collectionState.filePrimary.mtime ? new Date(collectionState.filePrimary.mtime).toLocaleString() : 'n/a'}</p>
                  <p><strong>Backup Updated:</strong> {collectionState.fileBackup.mtime ? new Date(collectionState.fileBackup.mtime).toLocaleString() : 'n/a'}</p>
                  <button
                    className="btn btn-warning"
                    disabled={!status.mongoConnected || restoringKey === collectionKey}
                    onClick={() => openRestoreCollectionModal(collectionKey)}
                  >
                    {restoringKey === collectionKey ? 'Restoring...' : 'Restore This Collection'}
                  </button>
                  {collectionState.snapshots && collectionState.snapshots.length > 0 && (
                    <div className="snapshot-history">
                      <h4>Snapshot History</h4>
                      {collectionState.snapshots.map((snapshot) => (
                        <div key={snapshot.snapshotId} className="snapshot-row">
                          <div className="snapshot-meta">
                            <div className="snapshot-id">{snapshot.snapshotId}</div>
                            <div>
                              {new Date(snapshot.createdAt).toLocaleString()} - {snapshot.count} records
                            </div>
                          </div>
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={!status.mongoConnected || restoringKey === collectionKey}
                            onClick={() => openRestoreCollectionModal(collectionKey, snapshot.snapshotId)}
                          >
                            Restore (select)
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeRestoreKey && status && createPortal(
        <div className="backup-modal-overlay" onClick={closeModal}>
          <div className="backup-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Restore</h3>
            {(() => {
              const collectionState = status.collections[activeRestoreKey];
              const snapshots = collectionState?.snapshots || [];
              if (snapshots.length === 0) return null;

              return (
                <div className="backup-version-selector">
                  <label htmlFor="backup-version-select"><strong>Select version to restore</strong></label>
                  <select
                    id="backup-version-select"
                    value={activeSnapshotId || ''}
                    onChange={(e) => setActiveSnapshotId(e.target.value || null)}
                  >
                    <option value="">Latest file backup/primary file</option>
                    {snapshots.map((snapshot) => (
                      <option key={snapshot.snapshotId} value={snapshot.snapshotId}>
                        {snapshot.snapshotId} - {new Date(snapshot.createdAt).toLocaleString()} ({snapshot.count} records)
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}
            <p>
              You are about to restore <strong>{COLLECTION_LABELS[activeRestoreKey] || activeRestoreKey}</strong>{' '}
              {activeSnapshotId ? (
                <>from snapshot <strong>{activeSnapshotId}</strong> into MongoDB.</>
              ) : (
                <>from file backup into MongoDB.</>
              )}
              This will overwrite current Mongo data for that collection.
            </p>
            <p>
              <strong>Requested version:</strong>{' '}
              {activeSnapshotId ? `Snapshot ID ${activeSnapshotId}` : 'Current file backup'}
            </p>
            <p>Type the restore phrase to confirm. The server checks it and does not display it.</p>
            <input
              type="text"
              value={typedPhrase}
              onChange={(e) => setTypedPhrase(e.target.value)}
              placeholder="Restore phrase"
              autoComplete="off"
            />
            <div className="backup-modal-actions">
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              <button
                className="btn btn-danger"
                onClick={handleRestore}
                disabled={typedPhrase.trim().length === 0 || restoringKey === activeRestoreKey}
              >
                Confirm Restore
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
};

export default BackupManagement;

